import {$, $$, isInteractiveElement} from './dom-utils.js';
import {getUABrand, getUAPlatform} from './utils.js';
import {locales} from './i18n.js';

let support = {
  fullscreen: !!document.body.requestFullscreen,
  pointerLock: !!document.body.requestPointerLock,
  keyboardLock: !!navigator.keyboard,
  wakeLock: !!navigator.wakeLock,
  pointerFine: matchMedia('(pointer: fine)').matches,
};
let env = {
  platform: getUAPlatform(),
  brand: getUABrand(),
};

export function main() {
  let messages = locales[document.documentElement.lang];
  const settings = {
    theme: 'black',
    autoLockPointer: true,
    autoLockKeys: true,
    autoKeepScreenAwake: false,
  };

  let refs = {
    themeInputs: $$('input[name="theme"]'),
    hdrVideo: $('#hdrVideo'),
    autoLockPointer: $('#autoLockPointer'),
    lockKeys: $('#lockKeys'),
    keepScreenAwake: $('#keepScreenAwake'),
  };

  let theme = matchMedia('(prefers-color-scheme: dark)').matches ? 'black' : 'white';
  settings.theme = theme;
  refs.themeInputs.forEach((input) => {
    input.checked = input.value === theme;
  });

  if (env.platform === 'iOS' && !support.fullscreen) {
    $$('#enterFullscreen,#requestPointerLock').forEach((e) => {
      e.disabled = true;
    });
  }

  if (!matchMedia('(dynamic-range: high) or (video-dynamic-range: high)').matches) {
    try {
      let radioInput = refs.themeInputs.find((e) => e.value === 'bright');
      radioInput.disabled = true;
      radioInput.parentNode.title = messages['hdr_image.not_supported'];
    } catch (e) {
      //
    }
  }
  if (!support.pointerLock && !support.pointerFine) {
    Object.assign(refs.autoLockPointer, {checked: false, disabled: true});
    refs.autoLockPointer.parentNode.title = messages['pointer_lock.not_supported'];
  }
  if (!support.keyboardLock) {
    Object.assign(refs.lockKeys, {checked: false, disabled: true});
    refs.lockKeys.parentNode.title = messages['keyboard_lock.not_supported'];
  }
  if (!support.wakeLock) {
    Object.assign(refs.keepScreenAwake, {checked: false, disabled: true});
    refs.keepScreenAwake.parentNode.title = messages['wake_lock.not_supported'];
  }
  refs.autoLockPointer.addEventListener('input', (e) => {
    settings.autoLockPointer = e.target.checked;
  });
  refs.lockKeys.addEventListener('input', (e) => {
    settings.autoLockKeys = e.target.checked;
  });
  refs.keepScreenAwake.addEventListener('input', (e) => {
    settings.autoKeepScreenAwake = e.target.checked;
  });
  let theme_changeHandler = (e) => {
    let theme = e.target.value;
    settings.theme = theme;
    document.documentElement.dataset.theme = theme;
    if (theme === 'bright') {
      let video = refs.hdrVideo;
      if (video.readyState === HTMLMediaElement.HAVE_NOTHING) {
        video.load();
      }
    }
  };
  refs.themeInputs.forEach((input) => {
    input.addEventListener('click', theme_changeHandler);
  });

  let keysLocked = null;
  let wakeLockPromise;

  let target = document.documentElement;
  let toggleFullscreen = (e) => {
    let modifierKey = env.platform === 'macOS' ? e.metaKey : e.ctrlKey;
    if (document.fullscreenElement === target) {
      if (modifierKey && support.pointerLock) { // toggle pointer lock if command/Ctrl key pressed while double click
        if (document.pointerLockElement) {
          document.exitPointerLock();
        } else {
          target.requestPointerLock();
        }
        return;
      } else {
        document.exitFullscreen();
      }
    } else {
      // Note: calling requestPointerLock() before requestFullscreen()
      // because the requestFullscreen() will consume the state of transient activation.
      if (support.fullscreen) {
        if (settings.autoLockPointer && support.pointerLock && !modifierKey) {
          target.requestPointerLock();
        }
        if (settings.autoLockKeys && support.keyboardLock) {
          keysLocked = navigator.keyboard.lock();
        }
        target.requestFullscreen({navigationUI: 'hide'});
        if (settings.autoKeepScreenAwake && support.wakeLock) {
          wakeLockPromise = new Promise((resolve) => {
            setTimeout(() => {
              if (document.fullscreenElement) {
                resolve(true);
              } else {
                resolve(false);
              }
            }, 5000);
          }).then((flag) => {
            if (flag)
              return navigator.wakeLock.request('screen');
            else
              return null;
          });
          wakeLockPromise.then((senitel) => {
            if (senitel) {
              console.debug(`acquired ${senitel} wake lock`);
            }
          }, (err) => {
            console.error(err);
          });
        }
      }
    }
  };
  $('#enterFullscreen').addEventListener('click', (e) => {
    e.stopPropagation();
    let button = e.target;
    button.style.pointerEvents = 'none';
    setTimeout(() => { button.style.pointerEvents = ''; }, 400);
    toggleFullscreen(e);
  });
  $('#requestPointerLock').addEventListener('click', (e) => {
    e.stopPropagation();
    let button = e.target;
    button.style.pointerEvents = 'none';
    setTimeout(() => { button.style.pointerEvents = ''; }, 400);
    if ( !document.pointerLockElement && support.pointerLock) {
      target.requestPointerLock();
    }
  });

  // Note: listening click event and checking event.detail instead of listening dblclick event
  // because requestPointerLock() may trigger NotAllowedError in dblclick event handler
  target.addEventListener('click', (e) => {
    if (e.detail !== 2 || isInteractiveElement(e.target))
      return;
    if (document.pointerLockElement) {
      document.exitPointerLock();
      return;
    }
    toggleFullscreen(e);
  });
  target.addEventListener('fullscreenchange', (e) => {
    if (document.fullscreenElement) {
      console.debug('enter fullscreen');
    } else {
      console.debug('exit fullscreen');
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
      if (keysLocked) {
        keysLocked.then(() => {
          navigator.keyboard.unlock();
        });
        keysLocked = null;
      }
      if (wakeLockPromise) {
        wakeLockPromise.then((senitel) => {
          if (senitel && !senitel.released) {
            senitel.release().then(() => {
              console.debug('released screen wake lock');
            });
          }
        });
        wakeLockPromise = null;
      }
    }
  });

  document.addEventListener('pointerlockchange', (e) => {
    if (document.pointerLockElement) {
      console.debug('enter pointerlock');
      if (!document.fullscreenElement) {
        target.classList.add('pointer-lock');
      }
      if (settings.theme === 'bright' && refs.hdrVideo.paused) {
        refs.hdrVideo.play();
      }
    } else {
      console.debug('exit pointerlock');
      target.classList.remove('pointer-lock');
    }
  });
  // prevent macOS from showing contextmenu when inputing ctrl + left_click
  if (env.platform === 'macOS') {
    document.addEventListener('contextmenu', (e) => {
      if (e.button === 0 && e.ctrlKey)
        e.preventDefault();
    });
  }
}
